import { supertypeClass, property, remote, amorphicStatic } from '@haventech/amorphic';
import { AppController } from '@hit/amorphic-types';

import _ from 'lodash';
import { MetadataNode, NoneNode, OtherNotSureNode } from './MetadataNode';
import { HITController } from './HITController';
import { Utils } from './Utils';
import { Insurers } from './static/Insurers';
import { Address } from './customer/Address';
import { FamilyHistory } from './customer/FamilyHistory';
import { PersonalHistory } from './customer/PersonalHistory';
import { TravelCountry } from './customer/TravelCountry';
import { Person } from './customer/Person';
import { ExistingPolicies } from './customer/ExistingPolicies';
import { Policy } from './customer/Policy';
import { RiskFactors } from './customer/RiskFactors';
import { MetadataApi } from '@hit/metadata-module';
import * as Handlebars from 'handlebars';
import { HavenAnalytics } from './HavenAnalytics';
import { MetadataCategory } from '@hit/metadata-module';
import { FlexibleObj } from './interfaces/FlexibleObj';
import { Beneficiary } from './customer/Beneficiary';
import { Product } from './static/Product';
import { CommonUtils } from './static/CommonUtils';
import { UICategory, UILoggerSingleton } from './logger/UILoggerSingleton';

declare var bindster;
declare var $;
declare var Bloodhound;

type NestedEntity = {
  name: string;
  node: MetadataNode;
};

@supertypeClass({ toClient: true, toServer: true })
export class ApplicationController extends AppController {
  @property({
    values: ['false', 'true'],
    descriptions: { false: 'I need an account', true: 'I already have an account' },
  })
  haveAccount: boolean;

  @property()
  agreesToTerms: boolean;

  @property()
  acknowledgementOwner: boolean;

  @property()
  acknowledgementInsured: boolean;

  @property({ rule: ['text', 'email', 'required'] })
  ownerEmail: string;

  @property()
  editingOwnerEmail: boolean;

  @property()
  ownerEmailChanged: boolean;

  @property()
  editingSecondaryEmail: boolean;

  @property({ rule: ['text', 'email', 'required'] })
  secondaryEmail: string;

  @property()
  secondaryEmailChanged: boolean;

  @property()
  validationCode: string;

  @property({ rule: ['zip5', 'required'], length: 5 })
  insuredZip: string;

  @property({ isLocal: true })
  questionsAnswered: number;

  @property({ isLocal: true })
  applicationReady: boolean;

  @property({ isLocal: true })
  firstRender: boolean;

  @property({ isLocal: true })
  rxNames;

  @property({ isLocal: true })
  notSupportedReason: string;

  @property({ isLocal: true })
  scrollEventSet: boolean;

  @property({ isLocal: true })
  applyRegisterReady: boolean;

  @property({ isLocal: true })
  isValidating: boolean;

  @property({ isLocal: true })
  isRegistering: boolean;

  @property({ isLocal: true })
  appMetaData: MetadataNode;

  @property({ isLocal: true })
  qsAnswered: FlexibleObj<boolean> = {};

  @property({ isLocal: true })
  topLevelQsCount: number;

  @property({ isLocal: true })
  currentBene: Beneficiary;

  @property({ isLocal: true })
  nextFocussable: string;

  @property({ isLocal: true })
  currentNodeSection: string;

  @property({ isLocal: true })
  nodes: FlexibleObj<MetadataNode> = {};

  @property({ isLocal: true, type: Array, getType: () => String })
  topLevelNodes: Array<string> = [];

  @property({ isLocal: true, type: Array, getType: () => String })
  topLevelQs: Array<string> = [];

  @property({ isLocal: true })
  enabledNodesCache: FlexibleObj<boolean> = {};

  @property({ isLocal: true, type: Array, getType: () => String })
  errorNodes: Array<string> = [];

  @property({ isLocal: true })
  errorMessages: FlexibleObj<Array<string>> = {};

  // Bloodhound is not a type it's a local var, that's why the type is any
  @property({ isLocal: true })
  bloodhounds: FlexibleObj<any>;

  @property({ getType: () => HITController })
  controller: HITController;

  constructor() {
    super();
  }

  static new(controller?) {
    return new ApplicationController().init(controller);
  }

  init(controller?) {
    this.controller = controller;
    return this;
  }

  reset() {}

  exit() {
    this.applicationReady = false;
    this.unsetScrollActions();
    this.firstRender = true;
  }

  enter(route?: string) {
    if (route) {
      const functionToCall = 'enter' + Utils.toCapitalCamelCase(route);
      if (typeof this[functionToCall] === 'function') {
        this[functionToCall]();
      }
    } else {
      const policy = this.controller.customer.application.primaryPolicy,
        options = {
          hierarchy: policy.getApplicationHierarchy(),
          criteria: policy.getAppQuestionsCriteria(),
        };

      this.scrollEventSet = false;
      return this.setupAppTree(options);
    }
  }

  enterApplyIntroRegister() {
    if (this.controller.loggedIn) {
      this.controller.route.public.apply_intro();
      return;
    }

    this.applyRegisterReady = false;

    const compileForm = function compileForm() {
      this.compileApplyRegisterForm();
      this.controller.refresh();
      this.applyRegisterReady = true;
    }.bind(this);

    setTimeout(compileForm, 100);
  }

  compileApplyRegisterForm() {
    const partialNames = $('script[type="text/x-handlebars-template"]')
      .get()
      .map(function (element) {
        return element.id;
      });

    _.each(partialNames, function (partialName) {
      const partialContent = $('#' + partialName).html();

      Handlebars.registerPartial(partialName, partialContent);
    });

    this.registerHandlebarsHelpers();

    const baseContent = $('#application-register-base').html();
    const baseTemplate = Handlebars.compile(baseContent);
    const html = baseTemplate(undefined);

    $('#apply-register-form').html(html);
  }

  continueApplication() {
    const routeId = this.controller['accountCenterController'].getApplicationRouteId();

    UILoggerSingleton.getInstance().debug(
      {
        module: 'applicationController',
        function: 'continueApplication',
        category: UICategory.milestone,
      },
      `routeId: ${routeId}`
    );

    if (routeId.match(/apply_form/)) {
      this.goToApplyForm();
    } else {
      this.controller.router.goToById(routeId);
    }
  }

  goToApplyForm() {
    const isOnMobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;

    if (isOnMobile && !this.controller.customer.mobileAppSawPopup) {
      this.controller.customer.mobileAppSawPopup = true;
      this.controller.route.public.dialog.application_mobile();
    } else {
      this.controller.route.private.apply_form();
    }
  }

  renderApplication() {
    this.toggleAppAssistant();
    this.applicationReady = true;
  }

  getApplicationLoadingClass() {
    if (this.applicationReady) {
      return 'application-ready';
    }

    return 'application-loading';
  }

  learnMoreClicked() {
    // Track for GA
    HavenAnalytics.set('event', {
      value: 'Left',
      propertyName: 'LearnMore',
    });
  }

  setScrollActions() {
    if (!this.scrollEventSet) {
      $(window).scroll(this.scrollProcessing.bind(this));
      $(window).resize(this.toggleAppAssistant.bind(this));

      this.scrollEventSet = true;
    }
  }

  unsetScrollActions() {
    $(window).off('scroll');
    $(window).off('resize');

    this.scrollEventSet = false;
  }

  showLogInForm() {
    this.haveAccount = true;
    this.controller.error = '';
    this.controller.refresh();
  }

  showRegisterForm() {
    this.haveAccount = false;
    this.controller.error = '';
    this.controller.refresh();
  }

  selectedGender() {
    return this.controller.customer.primaryCustomer.person.gender === '1' ? 'Male' : 'Female';
  }

  selectedAge() {
    return this.controller.customer.primaryCustomer.person.age + ' years old';
  }

  selectedHeight() {
    return (
      this.controller.customer.primaryCustomer.heightFeet +
      '’' +
      (this.controller.customer.primaryCustomer.heightInches
        ? this.controller.customer.primaryCustomer.heightInches + '"'
        : '')
    );
  }

  selectedWeight() {
    return this.controller.customer.primaryCustomer.weight + 'lbs';
  }

  selectedZip() {
    return this.controller.customer.primaryCustomer.address.zip;
  }

  selectedPartner() {
    return this.controller.customer.partnerName;
  }

  quoteSelected(row) {
    // if coming from quick quote, set corresponding dimensions/events
    if (this.controller.customer.primaryCustomer.overrideAmount) {
      HavenAnalytics.set('dimension', {
        InsuredAge: String(this.controller.customer.primaryCustomer.person.age),
        ZipCode: String(this.controller.customer.primaryCustomer.address.zip),
        InsuredGender: this.controller.customer.primaryCustomer.gender,
      });
      HavenAnalytics.set('metric', {
        FaceAmount: String(this.controller.customer.primaryCustomer.overrideAmount),
      });
      HavenAnalytics.set('event', {
        value: String(this.controller.customer.primaryCustomer.healthClassNumber),
        propertyName: 'InitialHealth',
      });
      HavenAnalytics.set('event', {
        value: String(this.controller.customer.primaryCustomer.smoker),
        propertyName: 'InitialHealth',
      });
    }

    this.controller.customer.setSelectedQuote(row);

    // Proceed only if the customer is in a state where we can sell HL insurance.
    this.insuredZip = this.controller.customer.primaryCustomer.address.zip;

    if (this.controller.isProductSoldIn(this.controller.customer.primaryCustomer.address.state)) {
      this.controller.customer.advanceApplicationProgress(10);

      // If this row is not equal to MM or HL render not_offered popup to prevent purchase
      if (!row.policies[0].isHaven) {
        this.controller.route.public.dialog.not_offered_yet();

        // Track for GA
        HavenAnalytics.set('event', {
          value: 'Any',
          propertyName: 'LearnMore',
        });

        HavenAnalytics.set('event', {
          value: Insurers.companies[row.policies[0].carrierCode].shortName,
          propertyName: 'LearnMore',
        });
      } else {
        if (this.controller.loggedIn) {
          this.introNext();
        } else {
          this.controller.route.public.apply_intro_register();
        }
      }
    } else {
      this.controller.route.public.dialog.invalid_state_quotes();
    }
  }

  introNext() {
    // For analytics
    HavenAnalytics.set('event', {
      value: String(this.controller.customer.selectedQuote.policies[0].face),
      propertyName: 'FinalFaceAmount',
    });

    HavenAnalytics.set('event', {
      value: String(this.controller.customer.selectedQuote.policies[0].term),
      propertyName: 'FinalTerm',
    });

    HavenAnalytics.set('event', {
      value: String(this.controller.customer.selectedQuote.policies[0].monthly),
      propertyName: 'FinalMonthlyPremium',
    });

    if (!this.controller.loggedIn) {
      this.controller.customer.advanceApplicationProgress(11);
      this.controller.route.public.apply_intro_register();
    } else {
      this.controller.customer.advanceApplicationProgress(12);

      this.createPolicy().then(
        function () {
          this.goToApplyForm();
        }.bind(this)
      );
    }

    this.controller.publicSave();
  }

  onLogout() {
    this.firstRender = true;
    this.haveAccount = null;
    this.agreesToTerms = null;
    this.acknowledgementOwner = null;
    this.acknowledgementInsured = null;
    this.editingOwnerEmail = null;
    this.ownerEmail = null;
    this.ownerEmailChanged = null;
    this.validationCode = null;
  }

  @remote({ on: 'server' })
  privateVerifyEmailFromCode(code: string): Promise<boolean> {
    // SecReviewed (modified since)
    const testMode =
      amorphicStatic.config.nconf.get('environment') !== 'production'
        ? amorphicStatic.config.nconf.get('test') || ''
        : '';
    if (testMode.match(/noverify/) && code === '1234') {
      this.controller.customer.emailValidated = true;

      return Promise.resolve(true);
    }

    return this.controller.privateVerifyEmailFromCode(code);
  }

  formNext() {
    const policy = this.controller.customer.application.primaryPolicy;
    // Set a policy variable that indicates that validation is ongoing...
    // This is needed to stop model triggers from running during validation. Typically
    // triggers should be idempotent but there are cases where this was not possible.
    policy.isValidating = true;

    this.controller.clearError(this, 'validationCode');

    if (!this.validateOwnerState()) {
      policy.isValidating = false;

      return;
    }

    if (!this.acknowledgementOwner) {
      policy.isValidating = false;

      this.controller.setError(this, 'acknowledgementOwner', 'This is required');

      if (!policy.isOwnerSameAsInsured() && !this.acknowledgementInsured) {
        this.controller.setError(this, 'acknowledgementInsured', 'This is required');
      }

      return;
    }

    if (!policy.isOwnerSameAsInsured() && !this.acknowledgementInsured) {
      policy.isValidating = false;

      this.controller.setError(this, 'acknowledgementInsured', 'This is required');

      return;
    }

    const defaultValidation = this.controller.validate();
    const appValidation = this.validateApplication();

    policy.isValidating = false;

    // Validation done
    if (defaultValidation && appValidation) {
      this.controller.isWaiting = true;
      this.postValidateApplication();
    }
  }

  public async postValidateApplication(): Promise<boolean> {
    const policy = this.controller.customer.application.primaryPolicy;
    policy.premiumPayerPerson = policy.getPremiumPayerPerson();
    policy.premiumPayerAddress = policy.getPremiumPayerAddress();
    policy.premiumPayerPhone = policy.getPremiumPayerPhone();

    if (this.editingOwnerEmail) {
      this.controller.newEmail = this.ownerEmail;
    }

    let isEmailValidated = false;
    if (this.controller.isAuth0Enabled && !this.controller.customer.emailValidated) {
      isEmailValidated = await this.controller.checkIfAccountVerified();
    } else {
      isEmailValidated = await this.validateEmail();
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'ApplicationController',
        function: 'postValidateApplication',
        data: {
          isAuth0Enabled: this.controller.isAuth0Enabled,
          policyNumber: policy.policyNumber,
        },
        category: UICategory.security,
      },
      'Post application validation'
    );

    if (isEmailValidated) {
      this.controller.verifyEmailCode = '';
      this.controller.isWaiting = isEmailValidated;

      // Validation of email successful?
      if (isEmailValidated) {
        this.copyInsuredToOwner(policy);

        policy.owner.email = this.controller.newEmail || this.controller.email;

        this.copySpouseInfo(policy);
        this.submitPolicy();
        return true;
      } else {
        this.controller.setError(this, 'validationCode', {
          message: this.validationCode ? 'bad_email_validation' : 'email_validation_required',
        });
        return false;
      }
    }
  }

  @remote({ on: 'server' })
  validateEmail(): Promise<boolean> {
    if (this.editingOwnerEmail) {
      this.controller.newEmail = this.ownerEmail;
    }

    return Promise.resolve().then(
      function () {
        // Verify the validation code if present but don't allow continuing since we need to validate
        if (!this.controller.customer.emailValidated) {
          return Promise.resolve()
            .then(
              function () {
                return this.privateVerifyEmailFromCode(this.validationCode);
              }.bind(this)
            )
            .then(
              function (success) {
                if (!success) {
                  return false;
                } else {
                  //update email of customer once email pin is verified.
                  // This is not being done in amorphic-userman because we are getting away on the practise of using userman for updating email of app
                  if (this.controller.customer.newEmail) {
                    const email =
                      (this.controller.customer.email =
                      this.controller.email =
                        this.controller.customer.newEmail);
                    this.controller.customer.newEmail = this.controller.newEmail = '';

                    const firstName = this.controller.customer.firstName;
                    const lastName = this.controller.customer.lastName;
                    const fullName = firstName + ' ' + lastName;
                    // Send an email changed confirmation email
                    this.controller.sendEmail('confirm_emailchange', email, email, fullName, [
                      { name: 'email', content: email },
                      { name: 'firstName', content: firstName },
                    ]);
                  }
                  return true;
                }
              }.bind(this)
            );
        } else {
          return true;
        }
      }.bind(this)
    );
  }

  copyInsuredToOwner(policy: Policy) {
    // If insured and owner are same, point owner to insured.
    // Have to do this as the final step right before submitting the policy,
    // otherwise changes to insured type or owner tpe in the application form
    // will break.
    if (policy.isOwnerSameAsInsured()) {
      policy.owner = policy.insured.person;
      policy.ownerPersonResidentialAddress = policy.insured.address;
      policy.ownerPersonPhone = policy.insured.phone;
    }
  }

  copySpouseInfo(policy: Policy) {
    CommonUtils.copySpouseInfo(policy);
  }

  submitPolicy() {
    return this.controller.customer.submitPolicy().then(
      function () {
        if (this.controller.customer.timeOfFirstVisit) {
          HavenAnalytics.set('dimension', {
            DaysSinceFirstVisit: String(
              Math.floor((new Date().getTime() - this.controller.customer.timeOfFirstVisit) / (1e3 * 60 * 60 * 24))
            ),
          });
        }

        const applicationState = this.controller.customer.application.primaryPolicy.getApplicationState();

        this.elasticPut(this.controller.customer.application.primaryPolicy, true);

        if (
          applicationState === 'CA' ||
          (applicationState === 'CT' &&
            this.controller.customer.application.primaryPolicy.getChannelCode() !== Product.channels.CAS.code) ||
          applicationState === 'RI'
        ) {
          this.controller.route.private.dialog.secondary_addressee();
        } else {
          this.controller.route.private.apply_submitted();
        }
      }.bind(this)
    );
  }

  elasticPut(policy: Policy, reindex: boolean): void {
    this.controller.elasticSend = true;

    if (this.controller.customer.application) {
      this.controller.elasticPut(this.controller.customer.application.primaryPolicy, reindex);
    }
  }

  validateApplication() {
    // Validate inter dependent fields
    let isError = false;
    const policy = this.controller.customer.application.primaryPolicy;

    if (policy.insured.familyHistory.motherLiving === 'isalive' && !policy.insured.familyHistory.motherAge) {
      this.controller.setError(this.controller.customer.application.primaryPolicy.insured.familyHistory, 'motherAge', {
        message: 'required',
      });

      isError = true;
    } else {
      this.controller.clearError(this.controller.customer.application.primaryPolicy.insured.familyHistory, 'motherAge');
    }

    if (policy.insured.familyHistory.fatherLiving === 'isalive' && !policy.insured.familyHistory.fatherAge) {
      this.controller.setError(this.controller.customer.application.primaryPolicy.insured.familyHistory, 'fatherAge', {
        message: 'required',
      });

      isError = true;
    } else {
      this.controller.clearError(this.controller.customer.application.primaryPolicy.insured.familyHistory, 'fatherAge');
    }

    if (
      policy.insured.personalHistory.primaryPhysicianLastVisitWhen === 'lastvisit' &&
      !policy.insured.personalHistory.primaryPhysicianLastVisit
    ) {
      this.controller.setError(
        this.controller.customer.application.primaryPolicy.insured.personalHistory,
        'primaryPhysicianLastVisit',
        {
          message: 'required',
        }
      );

      isError = true;
    } else {
      this.controller.clearError(
        this.controller.customer.application.primaryPolicy.insured.personalHistory,
        'primaryPhysicianLastVisit'
      );
    }

    if (policy.insured.person.hasDriversLicense !== false) {
      if (!policy.insured.person.idNumber) {
        this.controller.setError(this.controller.customer.application.primaryPolicy.insured.person, 'idNumber', {
          message: 'required',
        });

        isError = true;
      } else {
        this.controller.clearError(this.controller.customer.application.primaryPolicy.insured.person, 'idNumber');
      }

      if (!policy.insured.person.idState) {
        this.controller.setError(this.controller.customer.application.primaryPolicy.insured.person, 'idState', {
          message: 'required',
        });

        isError = true;
      } else {
        this.controller.clearError(this.controller.customer.application.primaryPolicy.insured.person, 'idState');
      }

      if (!policy.insured.person.idExpires) {
        this.controller.setError(this.controller.customer.application.primaryPolicy.insured.person, 'idExpires', {
          message: 'required',
        });

        isError = true;
      } else {
        this.controller.clearError(this.controller.customer.application.primaryPolicy.insured.person, 'idExpires');
      }
    }

    return !isError;
  }

  createPolicy() {
    return this.controller.customer
      .createPolicy(this.controller.customer.selectedQuote, this.controller.customer.getQuoteOptions().product.quoteKey)
      .then(
        function () {
          this.controller.customer.application.primaryPolicy.clientInit();

          return true;
        }.bind(this)
      );
  }

  clearPasswords() {
    this.controller.newPassword = '';
    this.controller.confirmPassword = '';
    this.controller.password = '';
  }

  editOwnerEmail() {
    this.ownerEmail = '';
    this.editingOwnerEmail = true;
    this.ownerEmailChanged = false;
  }

  cancelEditOwnerEmail() {
    this.clearPasswords();
    this.editingOwnerEmail = false;
  }

  editSecondaryEmail() {
    this.secondaryEmail = this.controller.customer.application.primaryPolicy.insured.person.email;
    this.editingSecondaryEmail = true;
  }

  cancelEditSecondaryEmail() {
    this.editingSecondaryEmail = false;
  }

  changeEmail() {
    this.controller.newEmail = this.ownerEmail;

    return this.controller
      .changeEmail(null, document.location.href)
      .then(
        function () {
          this.clearPasswords();

          this.editingOwnerEmail = false;
          this.ownerEmailChanged = true;

          return true;
        }.bind(this)
      )
      .catch(
        function (err) {
          if (err.text.match(/password/i)) {
            this.controller.setError(this.controller, 'password', err.text);
          } else {
            this.controller.setError(this, 'ownerEmail', err.text);
          }

          this.clearPasswords();

          return false;
        }.bind(this)
      );
  }

  changeSecondaryEmail() {
    this.controller.customer.application.primaryPolicy.insured.person.email = this.secondaryEmail;
    this.editingSecondaryEmail = false;
  }

  changeSecondaryEmailAndSave() {
    this.editingSecondaryEmail = false;

    return this.saveInsuredEmail();
  }

  @remote({ on: 'server' })
  saveInsuredEmail() {
    // SecReview Pending Todo: verify not save as owner's - Fixed.
    // Can be called from console. Make sure this can be changed only if the owner !== insured
    if (!this.controller.customer.application.primaryPolicy.isOwnerSameAsInsured()) {
      this.controller.customer.application.primaryPolicy.insured.person.email = this.secondaryEmail;
    }
  }

  validateOwnerState(suppressPopup?: boolean) {
    const policy = this.controller.customer.application.primaryPolicy;
    const address = policy.isOwnerSameAsInsured() ? policy.insured.address : policy.ownerPersonResidentialAddress;

    this.controller.clearError(policy.insured.address, 'zip');
    this.controller.clearError(policy.ownerPersonResidentialAddress, 'zip');

    if (!address.state) {
      return false;
    }

    if (!this.controller.isProductSoldIn(address.state)) {
      if (!suppressPopup) {
        this.controller.route.public.dialog.invalid_state_appl();
      }

      this.controller.setError(address, 'zip', 'Not yet sold here');

      return false;
    }

    return true;
  }

  setupZipValidation() {
    const applicationController = this;
    const policy = this.controller.customer.application.primaryPolicy;

    Address.inject(function () {
      this.streetValues = function () {
        const map = {};

        return _.filter(
          _.map(
            this.customer.addresses,
            function (addr) {
              if (addr.street && addr.state && !map[addr.getOneLineValue()]) {
                map[addr.getOneLineValue()] = true;

                return {
                  listValue: addr.getOneLineValue(),
                  fieldValue: addr.street,
                  address: [addr.street, addr.line1, addr.city, addr.zip],
                };
              } else {
                return null;
              }
            }.bind(this)
          ),
          function (o) {
            return !!o;
          }
        );
      };

      this.streetTypeaheadTrigger = function (obj) {
        this.street = obj.address[0];
        this.line1 = obj.address[1];

        if (this === policy.ownerPersonResidentialAddress) {
          bindster.DOMSet({
            bind: 'customer.application.primaryPolicy.ownerPersonResidentialAddress.zip',
            value: obj.address[3],
          });
        }

        if (this === policy.insured.address) {
          bindster.DOMSet({
            bind: 'customer.application.primaryPolicy.insured.address.zip',
            value: obj.address[3],
          });
        } else {
          this.zipSet(obj.address[3], true);
        }
      };

      this.zipSet = function (zip) {
        function reloadMetadata() {
          if (applicationController.controller.page.indexOf('apply_form') !== -1) {
            // Has Drivers license is used for validation against driverslicense to see if it should be checked for empty values.
            // Todo: remove the following line once the metadata sets default value to custom props, if the custom props is
            // not showing up in UI.( Ask Kumar or Sriram for more details )
            applicationController.controller.customer.application.primaryPolicy.insured.person.hasDriversLicense = true;

            // Simulate refresh on the application
            applicationController.exit();
            applicationController.enter();
          }
        }

        //Skip Zipcode Validation On Application Submit
        if (applicationController.controller.customer.application.primaryPolicy.isValidating) {
          return Promise.resolve();
        }

        const previousOwnerState = applicationController.getApplicationState();

        if (!zip && this === policy.ownerPersonResidentialAddress) {
          // Must be a reset of the owner address
          // Reload meta data
          reloadMetadata();
        }

        return applicationController.controller.publicGetLocationByZip(zip).then(
          function (location) {
            if (!location) {
              this.zip = zip;
              this.state = '';
              this.city = '';

              applicationController.controller.setError(this, 'zip', {
                message: 'Not a valid ZIP code',
              });
            } else {
              this.zip = zip;
              this.state = location.stateCode;
              this.city = location.city;
              this.latitude = location.lat;
              this.longitude = location.lng;
              this.timezone = location.timezone;

              const isInvalidZip =
                (this === policy.ownerPersonResidentialAddress &&
                  !applicationController.controller.isProductSoldIn(policy.ownerPersonResidentialAddress.state)) ||
                (this === policy.insured.address &&
                  policy.isOwnerSameAsInsured() &&
                  !applicationController.controller.isProductSoldIn(policy.insured.address.state));

              if (isInvalidZip) {
                applicationController.controller.setError(this, 'zip', {
                  message: 'Not sold here',
                });

                applicationController.controller.route.public.dialog.invalid_state_appl();
              }

              const ownerState = applicationController.getApplicationState();

              if (previousOwnerState !== ownerState) {
                reloadMetadata();
              }
            }
            applicationController.controller.refresh();
          }.bind(this)
        );
      };
    });
  }

  setDLState() {
    const policy = this.controller.customer.application.primaryPolicy;

    policy.insured.person.idState = policy.insured.address.state;
  }

  setupMisc() {
    const applicationController = this;
    const policy = this.controller.customer.application.primaryPolicy;

    FamilyHistory.inject(function () {
      const currentMLTrigger = FamilyHistory.prototype.motherLivingTrigger;

      this.motherLivingTrigger = function () {
        // Call existing trigger first
        if (currentMLTrigger) {
          currentMLTrigger.apply(this);
        }

        if (this.motherLiving !== 'isalive') {
          applicationController.controller.clearError(
            applicationController.controller.customer.application.primaryPolicy.insured.familyHistory,
            'motherAge'
          );
        }
      };

      const currentFLTrigger = FamilyHistory.prototype.fatherLivingTrigger;

      this.fatherLivingTrigger = function () {
        // Call existing trigger first
        if (currentFLTrigger) {
          currentFLTrigger.apply(this);
        }

        if (this.fatherLiving !== 'isalive') {
          applicationController.controller.clearError(
            applicationController.controller.customer.application.primaryPolicy.insured.familyHistory,
            'fatherAge'
          );
        }
      };
    });

    PersonalHistory.inject(function () {
      const currentTrigger = PersonalHistory.prototype.primaryPhysicianLastVisitWhenTrigger;

      this.primaryPhysicianLastVisitWhenTrigger = function () {
        // Call existing trigger first
        if (currentTrigger) {
          currentTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.customer.application.primaryPolicy.isValidating) {
          return;
        }

        if (this.primaryPhysicianLastVisitWhen === 'unknown') {
          this.primaryPhysicianLastVisit = null;
        }

        applicationController.controller.clearError(
          applicationController.controller.customer.application.primaryPolicy.insured.personalHistory,
          'primaryPhysicianLastVisit'
        );
      };

      const currentHIVTrigger = PersonalHistory.prototype.hivPositiveWhichAddressTrigger;

      this.hivPositiveWhichAddressTrigger = function () {
        // Call existing trigger first
        if (currentHIVTrigger) {
          currentHIVTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.customer.application.primaryPolicy.isValidating) {
          return;
        }

        if (this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'optout') {
          applicationController.notSupportedReason = 'hiv_optout';
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };
    });

    TravelCountry.inject(function () {
      this.noDepartureDateNYTrigger = function () {
        if (this.noDepartureDateNY) {
          this.departureDateNY = null;
          applicationController.controller.clearError(this, 'departureDateNY');
        }
      };
    });

    // Injecting triggers as they are view specific. All these triggers can eventually display a popup
    Person.inject(function () {
      const currentDOBTrigger = Person.prototype.dobTrigger;

      this.dobTrigger = function () {
        // Call existing trigger first
        if (currentDOBTrigger) {
          currentDOBTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (policy.isValidating) {
          return;
        }
        if (this.isPrimaryApplicant() && applicationController.controller.page.indexOf('private.') === 0) {
          const age = applicationController.controller.getAge(policy.insured.person.dob);

          if (age > 64 || age < 18) {
            applicationController.notSupportedReason = 'age';
            applicationController.controller.route.public.dialog.cannot_sell_policy();
          } else if (age > 49 && policy.term > 20) {
            //Show pop up cannot sell policy for 30 years for people of age > 49
            applicationController.notSupportedReason = 'higher_age_higher_term';
            applicationController.controller.route.public.dialog.cannot_sell_policy();
          }
        }
      };

      this.noTINTrigger = function () {
        // Used for clearing both owner and Insured TIN Errors in view model.
        if (this.noTIN) {
          this.TIN = '';
          applicationController.controller.clearError(this, 'TIN');
        }
      };

      this.isPrimaryApplicant = function () {
        return policy.customer && policy.customer.primaryCustomer && policy.customer.primaryCustomer.person === this;
      };

      const currentResTrigger = Person.prototype.residencyStatusTrigger;

      this.residencyStatusTrigger = function () {
        // Call existing trigger first
        if (currentResTrigger) {
          currentResTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.customer.application.primaryPolicy.isValidating) {
          return;
        }

        if (this.residencyStatus === 'none') {
          if (this === policy.insured.person) {
            applicationController.notSupportedReason = 'non-us';
          } else {
            applicationController.notSupportedReason = 'foreign-owner';
          }
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };

      const currentNoSSNTrigger = Person.prototype.noSSNTrigger;

      this.noSSNTrigger = function () {
        // Call existing trigger first
        if (currentNoSSNTrigger) {
          currentNoSSNTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.customer.application.primaryPolicy.isValidating) {
          return;
        }

        if (this.noSSN === true) {
          this.SSN = '';
          applicationController.controller.clearError(this, 'TIN');
          applicationController.notSupportedReason = 'noSSN';
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };

      const currentHasDriversLicenseTrigger = Person.prototype.hasDriversLicenseTrigger;

      this.hasDriversLicenseTrigger = function () {
        // Call existing trigger first
        if (currentHasDriversLicenseTrigger) {
          currentHasDriversLicenseTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.customer.application.primaryPolicy.isValidating) {
          return;
        }

        if (this.customer.application.primaryPolicy.insured.person.hasDriversLicense === false) {
          applicationController.controller.clearError(
            applicationController.controller.customer.application.primaryPolicy.insured.person,
            'idNumber'
          );
          applicationController.controller.clearError(
            applicationController.controller.customer.application.primaryPolicy.insured.person,
            'idState'
          );
          applicationController.controller.clearError(
            applicationController.controller.customer.application.primaryPolicy.insured.person,
            'idExpires'
          );
        }
      };
    });

    ExistingPolicies.inject(function () {
      const currentTrigger = ExistingPolicies.prototype.replaceInforceApplicationTrigger;

      this.replaceInforceApplicationTrigger = function () {
        // Call existing trigger first
        if (currentTrigger) {
          currentTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.customer.application.primaryPolicy.isValidating) {
          return;
        }

        if (this.replaceInforceApplication) {
          applicationController.notSupportedReason = 'replace';
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };
    });

    Policy.inject(function () {
      const currentReasonTrigger = Policy.prototype.reasonForInsuranceTrigger;

      this.reasonForInsuranceTrigger = function () {
        // Call existing trigger first
        if (currentReasonTrigger) {
          currentReasonTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.isValidating) {
          return;
        }

        if (['B', 'I', 'O', 'N', 'U'].indexOf(this.reasonForInsurance) > -1) {
          applicationController.notSupportedReason =
            this.__descriptions__('reasonForInsurance')[this.reasonForInsurance].toLowerCase();
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };

      const currentPremiumSourceTrigger = Policy.prototype.premiumSourceTrigger;

      this.premiumSourceTrigger = function () {
        // Call existing trigger first
        if (currentPremiumSourceTrigger) {
          currentPremiumSourceTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.isValidating) {
          return;
        }

        if (this.premiumSource) {
          applicationController.notSupportedReason = 'premiumSource';
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };

      const currentCollateralTrigger = Policy.prototype.collaterallyAssignedTrigger;
      this.collaterallyAssignedTrigger = function () {
        // Call existing trigger first
        if (currentCollateralTrigger) {
          currentCollateralTrigger.apply(this);
        }
        // Avoid running triggers during final validation
        if (this.isValidating) {
          return;
        }
        if (this.collaterallyAssigned) {
          applicationController.notSupportedReason = 'collateral';
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };
      const currentEconomicTrigger = Policy.prototype.economicIncentiveTrigger;

      this.economicIncentiveTrigger = function () {
        // Call existing trigger first
        if (currentEconomicTrigger) {
          currentEconomicTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.isValidating) {
          return;
        }

        if (this.economicIncentive) {
          applicationController.notSupportedReason = 'economic';
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };

      const currentBeneficialTrigger = Policy.prototype.beneficialInterestTrigger;

      this.beneficialInterestTrigger = function () {
        // Call existing trigger first
        if (currentBeneficialTrigger) {
          currentBeneficialTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.isValidating) {
          return;
        }

        if (this.beneficialInterest) {
          applicationController.notSupportedReason = 'commitment';
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };
    });

    RiskFactors.inject(function () {
      const currentTrigger = RiskFactors.prototype.armedForcesTrigger;

      this.armedForcesTrigger = function () {
        // Call existing trigger first
        if (currentTrigger) {
          currentTrigger.apply(this);
        }

        // Avoid running triggers during final validation
        if (this.customer.application.primaryPolicy.isValidating) {
          return;
        }

        if (this.armedForces) {
          applicationController.notSupportedReason = 'military';
          applicationController.controller.route.public.dialog.cannot_sell_policy();
        }
      };
    });
  }

  setCrossOverPolicy() {
    // Clone selectedQuote and update it
    const selectedQuote = this.controller.customer.selectedQuote;
    const cloneSelectedQuote = JSON.parse(JSON.stringify(this.controller.customer.selectedQuote));

    cloneSelectedQuote.policies[0].face = selectedQuote.policies[0].newFace;
    cloneSelectedQuote.policies[0].monthly = selectedQuote.policies[0].newMonthly;

    // No need to track newFace and monthly anymore.
    cloneSelectedQuote.policies[0].newFace = null;
    cloneSelectedQuote.policies[0].newMonthly = null;

    this.controller.customer.setSelectedQuote(cloneSelectedQuote);
    this.controller.publicSave();
  }

  toggleAppAssistant() {
    const appAssistant = $('.applicationAssistant');
    if (appAssistant.length) {
      if ($(window).width() < 991 || ($(window).height() < 725 && appAssistant.offset().top > 14e3)) {
        appAssistant.css('display', 'none');
      } else {
        appAssistant.css('display', 'inline-block');
      }
    }
  }

  scrollProcessing() {
    // When the 'topBleed' panel is scrolled past, show percent bar
    const topDiv = $('#topBleed');
    const percentDiv = $('#percentBar');

    if (topDiv.length > 0 && percentDiv.length > 0) {
      const windowPos = $(window).scrollTop();
      const topDivOffset = topDiv.offset();

      if (windowPos - topDiv.height() >= topDivOffset.top) {
        percentDiv.addClass('stick').show();
      } else {
        percentDiv.removeClass('stick').hide();
      }
    }
    // Look for left sections from the end of the document to the start.
    // The first one whose top is less than window's top should be fixed.
    const appAssistant = $('.questions .applicationAssistant');
    const appSections = $('.questions .app-section');

    $(appSections.get().reverse()).each(
      function (_index, element) {
        this.toggleAppAssistant();
        // Fix the section to the top.
        const currSection = $(element);
        const percentDiv = $('#percentBar');

        if (currSection.parent().offset().top <= $(window).scrollTop() + percentDiv.height()) {
          appSections.removeClass('stick');
          currSection.addClass('stick');
          appAssistant.addClass('stick');

          return false;
        } else {
          appSections.removeClass('stick');
          appAssistant.removeClass('stick');
        }
      }.bind(this)
    );
  }

  submitEnabled() {
    const answered = _.keys(this.qsAnswered);

    // Checks that all top questions have been answered
    const unanswered = _.difference(this.topLevelQs, answered);

    return !this.editingOwnerEmail && _.isEmpty(unanswered);
  }

  resendValidationCode() {
    const policy = this.controller.customer.application.primaryPolicy;

    policy.isValidating = true;

    $('#resend-code-btn').prop('disabled', true);
    $('#resend-code-btn').button('loading');

    this.controller.clearError(this, 'validationCode');

    this.controller.resendChangeEmailValidationCode(document.location.href).then(
      function () {
        policy.isValidating = false;
        $('#resend-code-btn').button('reset');
      }.bind(this)
    );
  }

  @remote({ on: 'server' })
  getMetadata(options, channel?: string, version?: number) {
    const opts = options || {};
    const category = opts.category || 'App_Questions';
    const hierarchy = Utils.array(opts.hierarchy);
    const dimensions = opts.dimensions ? [opts.dimensions] : [];

    const criteria = opts.criteria
      ? opts.criteria
      : {
          key: '^((?!B00253).)*$',
        };

    //Replace buildMetadata with ConstructMetadata only after updating appMetadataVersion of Policy, or else this would fail.
    return Promise.resolve(MetadataApi.buildMetadata(category, hierarchy, dimensions, criteria));
  }

  isError(id: string): boolean {
    return this.errorMessages[id] && this.errorMessages[id].length > 0;
  }

  setupAppTree(appOptions, channel?: string, metadataVersion?: number) {
    const buildApplication = function (metadata) {
      this.topLevelQsCount = 0;

      this.appMetaData = MetadataNode.new('', {
        level: 0,
      });

      this.controller.clearErrors();
      this.errorNodes = [];

      // Reset the list of errorNodes
      // Reset the toplevel nodes and questions so that setupapptree be called anytime of the app,
      // It rebuilds the whole app.
      this.topLevelNodes = [];
      this.topLevelQs = [];

      const appQuestions = metadata['App_Questions'];

      const nodes = this.nodes;
      const nestedEntities: FlexibleObj<Array<MetadataNode>> = {}; // e.g. Diagnosis -> [child node1, child node2....]
      const nestedEntity: NestedEntity = { name: null, node: null }; // If the current context is a nested entity
      let currentSection;

      const levelToNodeMap = {};

      // level -> last encountered node at that level
      let isSingleRow = false;

      //  flag to track if we want single row layout or not
      appQuestions.forEach(
        function (obj) {
          let condition;
          let showifExp;

          if (!obj.exclude_display) {
            if (obj.section && obj.section !== this.currentNodeSection) {
              currentSection = this.appMetaData.addChild(
                MetadataNode.new('', {
                  section: obj.section,
                  sectionOnEnter: obj.sectionOnEnter,
                  topLevel: true,
                })
              );

              this.currentNodeSection = obj.section;

              // If the current row is a section, setup its MetadataNode and
              // return (app_taking has section rows), otherwise
              // treat the row as a question and build the MetadataNode
              if (obj.control_type === 'section') {
                condition = obj.appears_based_on;
                showifExp = this.constructEvalExp(condition, nodes, obj.key);
                currentSection.showifExp = showifExp;
                currentSection.id = obj.key;
                currentSection.controlType = obj.control_type;
                nodes[currentSection.id] = currentSection;

                return;
              }
            }

            const entity = obj.entity;
            const name = obj.property;

            const controlType = obj.custom_control_type ? obj.custom_control_type : obj.control_type;

            if ('rowend' === obj.layout) {
              isSingleRow = false;
            }

            const options: any = {
              id: obj.key,
              qText: obj.question,
              qTranslate: obj.questionTranslate,
              permutation: obj.permutation,
              permutationAnswers: obj.permutationAnswers,
              controlType: controlType,
              widths: obj.widths,
              level: obj.question_level,
              helpText: obj.help_text,
              pronounLocked: obj.pronoun_locked,
              type: obj.type,
              required: obj.required,
              placeholder: obj.placeholder,
              customProps: obj.custom_control_properties,
              placeHolder: obj.placeholder,
              styleClass: obj.style_class,
              layout: isSingleRow ? 'rowbody' : obj.layout || '',
              width: obj.width,
              fid: obj.fill_id,
              validation: obj.client_validation,
              loadingCondition: obj.loading_condition,
              loadingConditionMessage: obj.loading_condition_message,
              inNestedEntity: false,
              relatedQuestions: obj.related_questions,
              iteratorMaxLength: obj.iterator_max_length,
              subProperty: '',
              iterateLinkLabel: obj.iterate_link_label,
              modalType: obj.modal_type,
            };

            if ('rowstart' === obj.layout) {
              isSingleRow = true;
            }

            if (obj.subproperty) {
              options.subProperty = _.compact([obj.model_path, obj.subproperty]).join('.');
            }

            let prop = _.compact([obj.model_path, obj.property]).join('.');

            condition = obj.appears_based_on;
            showifExp = this.constructEvalExp(condition, nodes, obj.key);

            // Should this entity be cloned? For e.g. we want all Diagnosis arrays to be cloned.
            const shouldClone = !(obj.clone && obj.clone.match(/No/i));

            // If there is a parent question, add the current question as a child
            if (!options.level) {
              UILoggerSingleton.getInstance().error(
                {
                  module: 'application',
                  function: 'setupAppTree',

                  category: UICategory.security,
                  error: {
                    isHumanRelated: false,
                  },
                },
                `Error with MetadataNode: Missing Level ${obj.key}`
              );

              throw new Error('Error with MetadataNode: Missing Level ' + obj.key);
            }

            const parentNode = String(options.level) === '1' ? currentSection : levelToNodeMap[options.level - 1];
            let currNode;

            options['showifExp'] = showifExp;

            // Set the level
            // If parent is a checkbox, uiLevel is parent's uiLevel + 1
            // If parent is a radiogroup, uiLevel is same as parent's uiLevel
            const parentControlType = parentNode.controlType || '';

            if (parentControlType.match(/checkbox|select/)) {
              options.uiLevel = parentNode.uiLevel + 1;
            } else if (parentControlType.match(/radio-group/)) {
              options.uiLevel = parentNode.uiLevel;
            } else if (parentControlType.match(/label/)) {
              if (options.level === '2') {
                options.uiLevel = 0;
              } else {
                options.uiLevel = parentNode.uiLevel + 1;
              }
            }

            if (entity !== nestedEntity.name) {
              nestedEntity.name = nestedEntity.node = null;
            }

            // Nested entities require special handling.
            if (nestedEntity.name && entity === nestedEntity.name) {
              if (nestedEntity.node.controlType.match(/iterate-collection/)) {
                if (entity !== 'Beneficiary') {
                  prop = 'current' + _.compact([entity, name]).join('.');
                }
              } else {
                prop = nestedEntity.node.prop + '.' + name;
              }

              options.inNestedEntity = true;
            }

            // OtherNotSureNode is only applicable to Checkboxes
            if (obj.question === 'None of the Above') {
              currNode = NoneNode.new(prop, options);
            } else if (
              obj.lastCB ||
              (obj.control_type.match(/^checkbox/) &&
                obj.question &&
                obj.question !== 'Other Airlines (non-scheduled passenger service)' &&
                (obj.question.match(/^Other/) || obj.question === 'Formula Kart Experiment (FKE)'))
            ) {
              currNode = OtherNotSureNode.new(prop, options);
            } else {
              currNode = MetadataNode.new(prop, options);
            }

            nodes[obj.key] = currNode;

            if (options.level === '1') {
              this.topLevelNodes.push(obj.key);

              if (!condition) {
                this.topLevelQs.push(obj.key);
                this.topLevelQsCount++;
              }
            }

            // Remember the entity nodes so that they can be reused.
            if (nestedEntity.name) {
              if (!nestedEntities[nestedEntity.name]) {
                nestedEntities[nestedEntity.name] = [];
              }

              nestedEntities[nestedEntity.name].push(currNode);

              if (nestedEntity.node.parent === parentNode) {
                currNode.resetLevel();
                nestedEntity.node.addChild(currNode, this.topLevelNodes[this.topLevelNodes.length - 1]);
              } else {
                currNode.resetLevel();
                parentNode.addChild(currNode, this.topLevelNodes[this.topLevelNodes.length - 1]);
              }
            } else {
              if (obj.type) {
                let nestedNodes;

                if (obj.type.match(/array *of *([A-Za-z]*)/)) {
                  nestedEntity.name = RegExp.$1;
                  nestedEntity.node = currNode;

                  // If the nested entity is already in the hash, add all its nodes to the
                  // current node (exclude Bene)
                  if (shouldClone && nestedEntities[nestedEntity.name]) {
                    nestedNodes = nestedEntities[nestedEntity.name];

                    nestedNodes.forEach(
                      function (theNode) {
                        const clone = theNode.shallowCopy();

                        clone.id = obj.key + '.' + theNode.id;

                        // Generate an id
                        currNode.addChild(clone, this.topLevelNodes[this.topLevelNodes.length - 1]);

                        clone.updateFid();
                        nodes[clone.id] = clone;
                      }.bind(this)
                    );
                  }
                } else if (!obj.type.match(/boolean|string|number|multivalue|date/i)) {
                  nestedEntity.name = obj.type;
                  nestedEntity.node = currNode;

                  // If the nested entity is already in the hash, add all its nodes to the
                  // current node
                  if (shouldClone && nestedEntities[nestedEntity.name]) {
                    nestedNodes = nestedEntities[nestedEntity.name];

                    nestedNodes.forEach(
                      function (theNode) {
                        const copyNode = theNode.shallowCopy();
                        const toks = theNode.prop.split('.');
                        copyNode.setProp(nestedEntity.node.prop + '.' + toks[toks.length - 1]);
                        copyNode.showifExp = showifExp;
                        currNode.addChild(copyNode, this.topLevelNodes[this.topLevelNodes.length - 1]);
                        nodes[copyNode.id] = copyNode;
                      }.bind(this)
                    );
                  }
                }
              }

              parentNode.addChild(currNode, this.topLevelNodes[this.topLevelNodes.length - 1]);
            }

            levelToNodeMap[options.level] = currNode;

            if (controlType === 'conditional-error-message') {
              this.errorNodes.push(currNode.id);
            }
          }
        }.bind(this)
      );

      const dummy = MetadataNode.new('', {
        id: '-1',
        controlType: 'label-basic',
        level: 1,
      });

      nodes[dummy.id] = dummy;

      this.topLevelNodes.push(dummy.id);

      currentSection.addChild(dummy, this.topLevelNodes[this.topLevelNodes.length - 1]);

      // Dummy
      this.firstRender = true;
      this.nodes = nodes;
      this.setupZipValidation();
      this.setupMisc();

      return Promise.resolve(this.appMetaData);
    }.bind(this);

    return this.getMetadata(appOptions, channel, metadataVersion).then(buildApplication);
  }

  constructEvalExp(condition: string, nodes: FlexibleObj<MetadataNode>, nodeId: string) {
    try {
      if (!condition) {
        return;
      }

      // First split the condition into multiple questions
      // based on the presence of AND or OR (only one
      // of them can exist for now)
      let conditions = condition.split(' OR ');
      const orPresent = conditions.length > 1;

      if (!orPresent) {
        conditions = condition.split(' AND ');
      }

      const evalStrings = [];

      conditions.forEach(
        function (current) {
          const currResult = this.parseQuestion(current);
          const parentQ = currResult.parentQ;
          const predicate = currResult.predicate;
          const operator = currResult.operator;
          const evalExp = currResult.evalExp;
          let unaryOp;
          let showif;
          let regex;

          if (typeof predicate === 'string') {
            const predicateSplit = predicate.split(' or ');

            if (predicateSplit.length === 1) {
              showif = '"' + predicate + '"';
            } else {
              // Construct a regex
              regex = true;
              showif = predicateSplit.join('|');
            }
          } else {
            showif = predicate;
          }

          // Construct eval expression
          if (evalExp) {
            evalStrings.push('controller.customer.application.primaryPolicy.' + evalExp);
          } else if (parentQ) {
            const parent = nodes[parentQ];
            const propName = parent.prop;

            if (regex) {
              showif = new RegExp(showif);
              unaryOp = operator === '!==' ? '!' : '';

              evalStrings.push(
                'controller.' + propName + ' && ' + unaryOp + 'controller.' + propName + '.match(' + showif + ')'
              );
            } else {
              if (typeof showif !== 'undefined') {
                evalStrings.push('controller.' + propName + operator + showif);
              } else {
                if (currResult.parentQ2) {
                  const parent2Prop = nodes[currResult.parentQ2].prop;

                  evalStrings.push('controller.' + propName + operator + 'controller.' + parent2Prop);
                } else {
                  // Must be a truthy/falsy check as there is no parent2.
                  unaryOp = operator === 'truthy' ? '' : '!';
                  evalStrings.push(unaryOp + 'controller.' + propName);
                }
              }
            }
          }
        }.bind(this)
      );

      return orPresent ? evalStrings.join(' || ') : evalStrings.join(' && ');
    } catch (e) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'ApplicationController',
          function: 'constructEvalExp',
          error: e,
          category: UICategory.request,
        },
        `Error with MetadataNode ${nodeId}`
      );

      throw e;
    }
  }

  parseQuestion(condition: string) {
    let parentQ,
      parentQ2,
      predicate,
      evalExp,
      operator = '===';

    if (!condition.match(/^if/i)) {
      condition = 'if ' + condition;
    }

    if (condition.match(/if Q([0-9a-z.]+) is (.*)/i)) {
      parentQ = RegExp.$1;
      predicate = RegExp.$2;
    } else if (condition.match(/if Q([0-9a-z.]+) = Yes$/i)) {
      parentQ = RegExp.$1;
      predicate = true;
    } else if (condition.match(/if Q([0-9a-z.]+) = No$/i)) {
      parentQ = RegExp.$1;
      predicate = false;
    } else if (condition.match(/if Q([0-9a-z.]+) = Q([0-9a-z]+)/i)) {
      parentQ = RegExp.$1;
      parentQ2 = RegExp.$2;
    } else if (condition.match(/if Q([0-9a-z.]+) = (.*)/i)) {
      parentQ = RegExp.$1;
      predicate = RegExp.$2;
    } else if (condition.match(/if Q([0-9a-z.]+) > (.*)/i)) {
      parentQ = RegExp.$1;
      predicate = Number(RegExp.$2);
      operator = '>';
    } else if (condition.match(/if Q([0-9a-z.]+) < (.*)/i)) {
      parentQ = RegExp.$1;
      predicate = Number(RegExp.$2);
      operator = '<';
    } else if (condition.match(/if ([0-9a-z.]+) = (.*)/i)) {
      evalExp = RegExp.$1 + '===' + RegExp.$2;
    } else if (condition.match(/([a-z]+\(\)$)/i)) {
      evalExp = RegExp.$1;
    } else if (condition.match(/If Q([0-9a-z.]+) and (.*)/)) {
      parentQ = RegExp.$1;
      evalExp = RegExp.$2;
      predicate = true;
    } else if (condition.match(/if Q([0-9a-z.]+) != Q([0-9a-z.]+)/i)) {
      parentQ = RegExp.$1;
      parentQ2 = RegExp.$2;
      operator = '!==';
    } else if (condition.match(/if Q([0-9a-z.]+) != (.*)/i)) {
      // E.g. If QB00014 != US
      parentQ = RegExp.$1;
      predicate = RegExp.$2;
      operator = '!==';
    } else if (condition.match(/if ([0-9a-z.]+) != (.*)/i)) {
      evalExp = RegExp.$1 + '!==' + RegExp.$2;
    } else if (condition.match(/if Q([0-9a-z.]+)/i)) {
      // Truthy
      parentQ = RegExp.$1;
      operator = 'truthy';
    } else if (condition.match(/if !Q([0-9a-z.]+)/i)) {
      // Falsy
      parentQ = RegExp.$1;
      operator = 'falsy';
    }

    return {
      parentQ: parentQ,
      parentQ2: parentQ2,
      predicate: predicate,
      operator: operator,
      evalExp: evalExp,
    };
  }

  isShown(id: string) {
    // Should the node with the given id be visible. Use the node's parent and check for the condition value
    if (this.appMetaData) {
      const node = this.nodes[id];

      if (node) {
        const visible = node.isShown();

        // Only reset non iterate props
        if (
          node.prop.indexOf('customer.application.primaryPolicy') === 0 ||
          node.controlType === 'section' ||
          node.controlType === 'checkbox-group-label'
        ) {
          // If node was previously shown and now hidden, reset it and its children
          if (node.isShowing && !visible) {
            node.resetVal();

            // The trigger for iterates must have already done the reset
            if (!node.controlType.match(/iterate-collection/)) {
              node.resetChildren();
            }
          }

          node.isShowing = visible;
        } else if (node.prop.indexOf('current') === 0) {
          // Iterate
          if (!visible) {
            node.resetVal();
            node.resetChildren();
          }
        }

        return visible;
      }
    }

    return false;
  }

  onchange(modelProp: string, value: boolean | number | string) {
    // Find the corresponding MetadataNode in the hierarchy
    let node;

    if (
      modelProp &&
      modelProp.indexOf('customer.application.primaryPolicy.') === 0 &&
      typeof value === 'boolean' &&
      value === true
    ) {
      if (this.appMetaData) {
        node = this.appMetaData.find(modelProp);

        this.handleNoneNode(node);
      }
    }

    // Analytics stuff
    if (
      modelProp &&
      modelProp.indexOf('customer.application.primaryPolicy.insured.personalHistory') === 0 &&
      typeof value === 'boolean'
    ) {
      if (this.appMetaData) {
        let medCount = this.controller.customer.application.primaryPolicy.insured.personalHistory.medicalConditionCount;

        if (value) {
          medCount++;
        } else if (medCount > 0) {
          medCount--;
        } else {
          medCount = 0;
        }

        this.controller.customer.application.primaryPolicy.insured.personalHistory.medicalConditionCount = medCount;

        HavenAnalytics.set('event', {
          value: String(
            this.controller.customer.application.primaryPolicy.insured.personalHistory.medicalConditionCount
          ),
          propertyName: 'NumHealthConditions',
        });
      }
    }

    HavenAnalytics.set('dimension', {
      ExitQuestion: modelProp,
    });
  }

  handleNoneNode(node: MetadataNode) {
    // If the value was reset from true to false, clear children
    if (node instanceof NoneNode) {
      // If the node is "None of the above", uncheck siblings
      for (let i = 0; i < node.parent.children.length; i++) {
        const child = node.parent.children[i];

        if (node === child) {
          break;
        } else {
          child.resetVal();
          child.resetChildren();
        }
      }
    } else {
      // If the value was set to true, find a corresponding none node and reset it.
      // Get the node's parent and check for a next sibling "None of the above"
      const none: MetadataNode = _.find(node.parent.children, function (child) {
        return child instanceof NoneNode;
      });

      if (none) {
        none.resetVal();
      }
    }
  }

  getClass(id: string, setFocus: boolean): string {
    const isEnabled = this.enabledNodesCache[id];

    if (setFocus) {
      // Use jquery to set appropriate focus (either to the first field in the subsequent
      // question or the last field of the current question)
      if (isEnabled) {
        if (this.nextFocussable === id) {
          $('#' + id + ' :input')
            .first()
            .focus();
          this.nextFocussable = null;
        }
      } else {
        if (this.nextFocussable === id) {
          const prevNode = this.findPrevLevel1NodeWhichIsVisible(this.nodes[id]);

          $('#' + prevNode.id + ' :input')
            .last()
            .focus();
          this.nextFocussable = null;
        }
      }
    }

    return isEnabled ? 'enabled1' : 'disabled1';
  }

  isEnabled(id: string): string {
    if (id === 'applyRegister') {
      return '';
    }

    return this.enabledNodesCache[id] ? '' : 'disabled';
  }

  setNextFocus(id: string) {
    this.nextFocussable = id;
  }

  setOptional(id: string, flag: boolean) {
    if (flag) {
      this.nodes[id].requiredProps[0] = 'no';
    } else {
      this.nodes[id].requiredProps[0] = 'yes';
    }
  }

  findPrevLevel1NodeWhichIsVisible(node: MetadataNode) {
    if (node) {
      const prev = node.findPrevLevel1Node();

      return prev.isShown() ? prev : this.findPrevLevel1NodeWhichIsVisible(prev);
    }

    return null;
  }

  handlePrerender() {
    this.buildEnabledQuestions();
    this.buildErrorMessages();
  }

  buildEnabledQuestions() {
    // Go through all the questions and refresh the enabled questions cache
    this.enabledNodesCache = {};
    this.qsAnswered = {};

    this.topLevelNodes.every(
      function (id) {
        const node = this.nodes[id];
        const prev = node.findPrevLevel1Node();

        if (!prev) {
          this.enabledNodesCache[id] = true;
        } else if (prev.isValid()) {
          this.enabledNodesCache[id] = true;

          if (!prev.showifExp) {
            this.qsAnswered[prev.id] = true;
          }
        } else {
          delete this.qsAnswered[prev.id];

          return false;
        }

        return true;
      }.bind(this)
    );
  }

  buildErrorMessages() {
    this.errorMessages = {};
    this.errorNodes.forEach(
      function (id) {
        const node = this.nodes[id];

        if (node && node.isShown()) {
          const text = node.qText;
          const relatedQs = node.relatedQuestions;

          relatedQs.forEach(
            function (relatedId) {
              if (!this.errorMessages[relatedId]) {
                this.errorMessages[relatedId] = [];
              }

              this.errorMessages[relatedId].push(text);
            }.bind(this)
          );
        }
      }.bind(this)
    );
  }

  handleRender() {
    if (this.firstRender) {
      this.firstRender = false;
      this.compileApplication();
      this.controller.refresh();
    }

    // Set scroll handling only when the application render has been completed
    // which can be checked by the presense of "Get my decision" button
    if ($('#appsubmit').length > 0 && $('#appsubmit').offset().top > 0) {
      this.setScrollActions();
      (<any>$).scrollDepth();
    }
  }

  percentProgress() {
    const qsAnsweredCount = Object.keys(this.qsAnswered).length || 0;
    const percentFilled = Math.round((qsAnsweredCount / this.topLevelQsCount) * 100) / 100;

    this.controller.customer.percentFilled = percentFilled > 1 ? 1 : percentFilled;

    HavenAnalytics.set('dimension', {
      QuestionsAnswered: String(this.controller.customer.percentFilled),
    });

    return this.controller.customer.percentFilled;
  }

  triggerFor(prop: string) {
    if (typeof prop !== 'string') {
      return;
    }

    const functionExpression = 'controller.' + prop + 'Trigger';
    const invokeFunctionExpression = functionExpression + '()';
    const triggerExpression = 'if(' + functionExpression + '){' + invokeFunctionExpression + '}';

    // TODO: Necessary to identify the Trigger coming from client
    //tslint:disable-next-line
    eval(triggerExpression);
  }

  resetFieldValue(prop: string, value: number | string | boolean) {
    const model = this.getModelFor(prop);
    const propString = this.getPropStringFor(prop);

    model[propString] = value;
  }

  changeFocusTo(prop: string, key: string, classSelector: string) {
    if (this.radioHasTextField(prop, key)) {
      $(classSelector).focus();
    }
  }

  getModelFor(prop: string) {
    const propParts = prop.split('.');
    const modelString = propParts.slice(0, -1).join('.');

    // TODO: Currently necessary for the evaluation of front properties passed in
    //tslint:disable-next-line
    var model = modelString === '' ? eval('controller') : eval('controller.' + modelString);

    return model;
  }

  getModelStringFor(prop: string) {
    const propParts = prop.split('.');
    const modelString = propParts.slice(0, -1).join('.');

    return modelString === '' ? 'controller' : 'controller.' + modelString;
  }

  getPropStringFor(prop: string) {
    const propParts = prop.split('.');
    const propString = propParts[propParts.length - 1];

    return propString;
  }

  getValuesFor(prop: string) {
    const model = this.getModelFor(prop);
    const propString = this.getPropStringFor(prop);
    const result = model.__values__(propString);
    const values = 'controller.' + prop + 'Values';

    if (!result) {
      // TODO: Eval necessary for the evaluation of properties (e.g. bloodhound)
      //tslint:disable-next-line
      return typeof eval(values) === 'function' ? eval(values + '()') : eval(values);
    }

    return result;
  }

  getDescriptionsFor(prop: string) {
    const model = this.getModelFor(prop);
    const propString = this.getPropStringFor(prop);

    return model.__descriptions__(propString);
  }

  nameFor(fid: string, collection: Array<string>) {
    if (!collection) {
      return fid;
    }
  }

  radioHasTextField(prop: string, key: string) {
    const descriptions = this.getDescriptionsFor(prop);

    if (!descriptions) {
      return false;
    }

    const description = descriptions[key];

    if (!description) {
      return false;
    }

    return !!description.match(/__([A-Za-z0-9_ ]+)/);
  }

  descriptionWidthFor(prop: string) {
    const descriptions = this.getDescriptionsFor(prop);

    return descriptions.width;
  }

  descriptionTextFor(prop: string, key: string) {
    const descriptionFunctionPath = 'controller.' + prop + 'Description';
    const descriptionFunctionInvoked = descriptionFunctionPath + '(' + key + ').text';
    const descriptionCollectionPath =
      this.getModelStringFor(prop) + '.__descriptions__("' + this.getPropStringFor(prop) + '")["' + key + '"].text';

    return this.evaluateFunctionExpression(
      descriptionFunctionPath,
      descriptionFunctionInvoked,
      descriptionCollectionPath
    );
  }

  descriptionFor(prop: string, key: string) {
    let textFieldMatch = '';

    if (this.radioHasTextField(prop, key)) {
      textFieldMatch = '.match(/__([A-Za-z0-9_ ]+)/)[1]';
    }

    const descriptionFunctionPath = 'controller.' + prop + 'Description';
    const descriptionFunctionInvoked = descriptionFunctionPath + '("' + key + '")';
    const descriptionCollectionPath =
      this.getModelStringFor(prop) +
      '.__descriptions__("' +
      this.getPropStringFor(prop) +
      '")["' +
      key +
      '"]' +
      textFieldMatch;

    return this.evaluateFunctionExpression(
      descriptionFunctionPath,
      descriptionFunctionInvoked,
      descriptionCollectionPath
    );
  }

  evaluateFunctionExpression(functionExp: string, invokedFunctionExp: string, collectionExp: string) {
    // TODO: Evaluations necessary to assess descriptions coming in from handlebars/html
    //tslint:disable-next-line
    if (typeof eval(functionExp) === 'function') {
      //tslint:disable-next-line
      return eval(invokedFunctionExp);
    } else {
      //tslint:disable-next-line
      return eval(collectionExp);
    }
  }

  removeIterateElement(prop: string, ix: number) {
    // TODO: Necessary to evaluate property for iterate element removal
    //tslint:disable-next-line
    var propCollection = eval('controller.' + prop);
    const element = propCollection[ix];

    propCollection.splice(ix, 1);

    if (typeof element.setRatios === 'function') {
      element.setRatios(propCollection);
    }
  }

  addIterateElement(prop: string, arrayType: string, initModel: string) {
    // TODO: Currently necessary for the evaluation of front end bindings
    //tslint:disable-next-line
    var propCollection = eval('controller.' + prop);
    let newElement;

    if (initModel) {
      //tslint:disable-next-line
      newElement = eval('RemoteObjectTemplate.__dictionary__.' + arrayType + '.new(' + initModel + ')');
    } else {
      //tslint:disable-next-line
      newElement = eval('RemoteObjectTemplate.__dictionary__.' + arrayType + '.new()');
    }

    propCollection.push(newElement);
  }

  showHideBeneficiary(current: Beneficiary) {
    if (this.currentBene) {
      if (this.currentBene === current) {
        this.currentBene = null;

        return;
      }
    }

    this.currentBene = current;
  }

  beneficiaryTypeDescription(type: string) {
    const policy = this.controller.customer.application.primaryPolicy;

    return type.match(/primaryBeneficiary/)
      ? policy.__values__('beneficiaryType')()[policy.beneficiaryType]
      : policy.__values__('contingentBeneficiaryType')()[policy.contingentBeneficiaryType];
  }

  initBloodhound(prop: string, id: string) {
    if (typeof Bloodhound === 'undefined') {
      return;
    }

    if (!this.bloodhounds) {
      this.bloodhounds = {};
    }

    let dataset = null;
    let resourceUrl = null;

    if (prop.indexOf('.rxName') > -1 && !this.rxNames) {
      resourceUrl = '/common/js/static/rxNames.json';
    } else if (prop.indexOf('.rxName') > -1) {
      dataset = this.rxNames;
    } else {
      dataset = this.getValuesFor(prop);
    }

    const options = this.getBloodhoundOptions(dataset, resourceUrl, prop);

    const bloodhound = new Bloodhound(<any>options);

    // Constructs the suggestion engine
    const trigger = 'controller.' + prop + 'TypeaheadTrigger';

    this.bloodhounds[id] = bloodhound;

    if ($('.typeahead' + id).length === 2) {
      $('.typeahead' + id)
        .parent()
        .replaceWith(
          $('.typeahead' + id)
            .parent()
            .children()
            .first()
            .next()
        );
      $('.typeahead' + id).css('position', 'static');
      $('.typeahead' + id).css('display', 'inline-block');
      $('.typeahead' + id).css('background', '');
    }

    let node = $('.typeahead' + id).get(0);

    $('.typeahead' + id)
      .typeahead(null, this.getTypeaheadOptions(prop, id, bloodhound))
      .on('typeahead:active', function (_ev, _suggestion) {
        if ($('.typeahead' + id).length === 2) {
          $('.typeahead' + id)
            .get(0)
            .removeAttribute('b:bind');
          $('.typeahead' + id)
            .get(0)
            .removeAttribute('bind');
          (<any>$('.typeahead' + id).get(0)).bindster = null;
          node = $('.typeahead' + id).get(1);
        }
      })
      .on('typeahead:selected', function (ev, suggestion) {
        setValue(ev, suggestion);
      })
      .on('typeahead:autocomplete', function (ev, suggestion) {
        setValue(ev, suggestion);
      });

    function setValue(_ev, _suggestion) {
      node.onchange(<any>{
        target: node,
      });

      // TODO: Necessary to evaluate the properties passed from the front end.
      //tslint:disable-next-line
      if (typeof eval(trigger) === 'function') {
        //tslint:disable-next-line
        eval(trigger + '(_suggestion)');
      }
    }
  }

  getTypeaheadOptions(prop: string, id: string, bloodhound) {
    if (prop.indexOf('.rxName') > -1) {
      return {
        name: id,
        templates: {
          suggestion: function (value) {
            return '<div>' + value + '</div>';
          },
        },
        source: bloodhound,
      };
    } else {
      return {
        name: id,
        display: 'fieldValue',
        templates: {
          suggestion: function (value) {
            return '<div>' + value.listValue + '</div>';
          },
        },
        source: bloodhound,
      };
    }
  }

  getBloodhoundOptions(dataset, resourceUrl?: string, prop?: string) {
    if (prop.indexOf('.rxName') > -1 && resourceUrl) {
      return {
        datumTokenizer: Bloodhound.tokenizers.whitespace,
        queryTokenizer: Bloodhound.tokenizers.whitespace,
        prefetch: {
          url: resourceUrl,
          transform: (response) => {
            this.rxNames = response;
            return response;
          },
        },
      };
    } else if (prop.indexOf('.rxName') > -1) {
      return {
        datumTokenizer: Bloodhound.tokenizers.whitespace,
        queryTokenizer: Bloodhound.tokenizers.whitespace,
        local: dataset,
      };
    } else {
      return {
        datumTokenizer: Bloodhound.tokenizers.obj.whitespace('listValue'),
        queryTokenizer: Bloodhound.tokenizers.whitespace,
        local: dataset,
      };
    }
  }

  updateBloodhound(prop: string, node: HTMLElement) {
    if (!this.bloodhounds) {
      return;
    }
    const isNotDynamic = prop.indexOf('.rxName') > -1;
    const id = node.className.replace(/^typeahead/, '').replace(/ .*/, '');
    const bloodhound = this.bloodhounds[id];

    if (!bloodhound || isNotDynamic) {
      return;
    }

    bloodhound.clear();

    const dataset = this.getValuesFor(prop);

    bloodhound.add(dataset);
  }

  setupDateMasks() {
    this.setupInputMasks({
      classSelector: '.date-type',
      mask: 'mm/dd/yyyy',
    });
  }

  setupShortDateMasks() {
    this.setupInputMasks({
      classSelector: '.sdate-type',
      mask: 'mm/yyyy',
    });
  }

  setupYearMasks() {
    if (!(<any>$.fn).inputmask) {
      return;
    }
    (<any>$('.year-type')).inputmask('y', {
      postValidation: function (buffer) {
        return parseInt(buffer.join('')) <= new Date().getFullYear();
      },
      placeholder: 'yyyy',
    });
  }

  setupSSNMasks() {
    this.setupInputMasks({
      classSelector: '.ssn-type',
      mask: '999-99-9999',
    });
  }

  setupEINMasks() {
    this.setupInputMasks({
      classSelector: '.ein-type',
      mask: '99-9999999',
    });
  }

  setupInputMasks(opts) {
    if (!(<any>$.fn).inputmask) {
      return;
    }

    const classSelector = opts.classSelector;
    const mask = opts.mask;

    // Set global inputMask defaults
    const options = {
      showMaskOnHover: false,
    };

    (<any>$(classSelector)).inputmask(mask, options);
  }

  compileApplication() {
    const startTime = Date.now();

    if (!this.appMetaData.children || this.appMetaData.children.length === 0) {
      return;
    }

    const partialNames = $('script[type="text/x-handlebars-template"]')
      .get()
      .map(function (element) {
        return element.id;
      });

    _.each(partialNames, function (partialName) {
      const partialContent = $('#' + partialName).html();

      Handlebars.registerPartial(partialName, partialContent);
    });

    this.registerHandlebarsHelpers();

    const baseContent = $('#application-base').html();
    const baseTemplate = Handlebars.compile(baseContent);

    const html = baseTemplate({
      sections: this.appMetaData.children,
    });

    $('#application-form').html(html);
    UILoggerSingleton.getInstance().debug(
      {
        module: 'applicationController',
        function: 'compileApplication',
        data: {
          compileTime: Date.now() - startTime,
        },
        category: UICategory.request,
      },
      `compileApplication`
    );
  }

  isInAdmin() {
    return false;
  }

  registerHandlebarsHelpers() {
    const self = this;

    Handlebars.registerHelper('isMatch', function (lVal, regex, _options) {
      return lVal && lVal.match(new RegExp(regex));
    });

    Handlebars.registerHelper('isEqual', function (v1, v2, _options) {
      return v1 === v2;
    });

    Handlebars.registerHelper('isNotEqual', function (lVal, rVal, _options) {
      return lVal !== rVal;
    });

    Handlebars.registerHelper('minus', function (op1, op2, _options) {
      return Number(op1) - Number(op2);
    });

    // Show interactive elements by default
    Handlebars.registerHelper('showInteractions', function () {
      return !(self.controller.applicationController && self.controller.applicationController.isInAdmin());
    });

    Handlebars.registerHelper('concat', function () {
      let outStr = '';

      for (const arg in arguments) {
        if (typeof arguments[arg] !== 'object') {
          outStr += arguments[arg];
        }
      }

      return outStr;
    });

    Handlebars.registerHelper('getFormName', function () {
      return this.getApplicationFormName(self.controller.customer.application.primaryPolicy);
    });
  }

  getApplicationState() {
    const policy = this.controller.customer.application.primaryPolicy;

    return policy.getApplicationState();
  }

  isICCApplication(policy: Policy) {
    if (!policy) {
      policy = this.controller.customer.application.primaryPolicy;
    }

    if (policy) {
      return policy.isICCApplication();
    }
  }

  getApplicationFormName(policy: Policy) {
    if (!policy) {
      policy = this.controller.customer.application.primaryPolicy;
    }

    return CommonUtils.getApplicationFormName(policy);
  }
}
